import { useEffect, useMemo, useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import HistoryIcon from "@mui/icons-material/History";
import SaveIcon from "@mui/icons-material/Save";
import TemplateField from "./TemplateField";
import ToothChart, { DEFAULT_FINDINGS } from "./ToothChart";
import ReferralChips, { DEFAULT_DEPARTMENTS } from "./ReferralChips";
import HistoryPanel, { fmt } from "./HistoryPanel";
import PrintSheet from "./PrintSheet";
import { api } from "../api/client";
import "./DentalEmrStyles.css";
import { successAlert } from "../../../../common/components/Toasts/CustomToasts";
import ConfirmationModal from "../../../../common/components/ConfirmationModal";

const EMPTY = {
  chiefComplaint: "",
  hopi: "",
  pastHistory: "",
  otherInfo: "",
  physicalExam: "",
  generalExam: "",
  teethNotes: "",
  periodontium: "",
  oralMucosa: "",
  tmj: "",
  diagnosis: "",
  teeth: [],
  referrals: [],
};

/** Build the auto "past history" text from previous saved visits. */
function summarizeHistory(past) {
  return past
    .map((h) => {
      const parts = [];
      if (h.chiefComplaint)
        parts.push("C/o " + h.chiefComplaint.split("\n")[0]);
      if (h.diagnosis) parts.push("Dx: " + h.diagnosis.split("\n")[0]);
      if (h.teeth?.length)
        parts.push("Teeth: " + h.teeth.map((t) => t.palmerCode).join(", "));
      if (h.referrals?.length)
        parts.push("Ref: " + h.referrals.map((r) => r.deptCode).join(" → "));
      return `${fmt(h.visitDate)} – ${parts.join("; ") || "visit recorded"}`;
    })
    .join("\n");
}

/**
 * Normalise the visit object passed by the HIMS host screen (keys as the host sends them,
 * e.g. "PatientId", "MRNo", "Patient Name", "Doctor Id", "VisitId", "Gender", "AgeYear" ...)
 */
export function fromHimsVisit(v) {
  if (!v) return null;
  return {
    id: v.PatientId,
    mrNo: v.MRNo ?? null,
    name: v["Patient Name"] ?? v.PatientName ?? "",
    age: v.AgeYear ?? null,
    gender: v.Gender ?? null,
    genderId: v.GenderId ?? null,
    visitId: v.VisitId,
    visitDate: v.Date ?? null,
    doctorId: v["Doctor Id"] ?? v.DoctorId ?? null,
    doctorName: v["Doctor Name"] ?? v.DoctorName ?? null,
    unitId: v.UnitId ?? null,
    departmentId: v.DepartmentId ?? null,
    department: v.Department ?? null,
    mlc: !!v["Medico Legal Case"],
  };
}

/** Map a saved DentalEmr (API shape) onto form state. */
function fromApi(e) {
  return {
    chiefComplaint: e.chiefComplaint || "",
    hopi: e.hopi || "",
    pastHistory: e.pastHistory || "",
    otherInfo: e.otherInfo || "",
    physicalExam: e.physicalExam || "",
    generalExam: e.generalExam || "",
    teethNotes: e.teethNotes || "",
    periodontium: e.periodontium || "",
    oralMucosa: e.oralMucosa || "",
    tmj: e.tmj || "",
    diagnosis: e.diagnosis || "",
    teeth: (e.teeth || []).map(({ palmerCode, findingCode, note }) => ({
      palmerCode,
      findingCode,
      note,
    })),
    referrals: (e.referrals || []).map(({ deptCode }) => ({ deptCode })),
  };
}

const modalBox = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90vw",
  height: "90vh",
  bgcolor: "#f3f5f8",
  borderRadius: 2,
  boxShadow: 24,
  outline: "none",
  overflow: "hidden",
};

/**
 * Dental EMR – MUI Modal.
 * Props:
 *   open      boolean
 *   visit     the visit row from the HIMS screen ({ PatientId, MRNo, "Patient Name", VisitId, "Doctor Id", ... })
 *   onClose   () => void
 *   onSaved   (emr) => void   optional
 *
 * Opening the modal for a visit that was saved earlier loads that sheet for editing;
 * Save then updates the same DentalEmr row (PUT) instead of creating a new one.
 */
export default function DentalEmrModal({ open, visit, onClose, onSaved }) {
  const patient = useMemo(() => fromHimsVisit(visit), [visit]);
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="dental-emr-title"
      keepMounted={false}
    >
      <Box sx={modalBox}>
        {open && patient && (
          <DentalEmrSheet
            patient={patient}
            visitId={patient.visitId}
            onClose={onClose}
            onSaved={onSaved}
          />
        )}
      </Box>
    </Modal>
  );
}

/** The sheet itself (can also be rendered outside the modal). `patient` is the normalised object from fromHimsVisit(). */
export function DentalEmrSheet({ patient, visitId, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [emrId, setEmrId] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [masters, setMasters] = useState({ findings: [], depts: [] });
  const [status, setStatus] = useState("");
  const [openPost, setOpenPost] = useState(false);

  const handleClosePost = () => {
    if (openPost === true) {
      setOpenPost(false);
    }
  };
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const findingName = (code) =>
    masters.findings.find((f) => f.findingCode === code)?.findingName ||
    DEFAULT_FINDINGS.find((f) => f.key === code)?.label ||
    code;
  const deptName = (code) =>
    masters.depts.find((d) => d.deptCode === code)?.deptName ||
    DEFAULT_DEPARTMENTS.find((d) => d.code === code)?.name ||
    code;
  const pastCount = history.filter((h) => h.visitId !== visitId).length;

  // ---- load masters, this visit's sheet (if saved) and past history ----
  useEffect(() => {
    let alive = true;
    (async () => {
      const [findings, depts] = await Promise.all([
        api.toothFindings().catch(() => []),
        api.departments().catch(() => []),
      ]);
      if (alive) setMasters({ findings: findings || [], depts: depts || [] });

      const [current, hist] = await Promise.all([
        api.emrForVisit(patient.id, visitId).catch(() => null),
        api.emrHistory(patient.id).catch(() => []),
      ]);
      if (!alive) return;
      const past = (hist || []).filter((h) => h.visitId !== visitId);
      setHistory(hist || []);
      if (current) {
        setEmrId(current.dentalEmrId);
        setForm({ ...EMPTY, ...fromApi(current) });
        setStatus(
          `Editing saved EMR #${current.dentalEmrId} · last updated ${new Date(current.updatedAt).toLocaleString()}`,
        );
      } else {
        setForm({ ...EMPTY, pastHistory: summarizeHistory(past) }); // history auto-populated
        setStatus(
          past.length
            ? `${past.length} previous visit(s) · last ${fmt(past[0].visitDate)}`
            : "First dental visit",
        );
      }
    })();
    return () => {
      alive = false;
    };
  }, [patient.id, visitId]);

  const payload = useMemo(
    () => ({
      patientId: patient.id,
      visitId,
      mrNo: patient.mrNo ?? null,
      patientName: patient.name,
      patientAge: patient.age ?? null,
      patientGender: patient.gender ?? null,
      doctorId: patient.doctorId ?? null,
      doctorName: patient.doctorName ?? null,
      unitId: patient.unitId ?? null,
      departmentId: patient.departmentId ?? null,
      ...form,
      referrals: form.referrals.map((r) => ({ deptCode: r.deptCode })),
    }),
    [form, patient, visitId],
  );

  const save = async () => {
    setStatus("Saving…");
    try {
      const saved = emrId
        ? await api.updateEmr(emrId, payload)
        : await api.saveEmr(payload);
      setEmrId(saved.dentalEmrId);
      setHistory((h) => [
        saved,
        ...h.filter((x) => x.dentalEmrId !== saved.dentalEmrId),
      ]);
      setStatus(
        `${emrId ? "Updated" : "Saved"} · EMR #${saved.dentalEmrId} · ${new Date().toLocaleTimeString()}`,
      );
      successAlert(`EMR ${emrId ? "Updated" : "Saved"} Successfully..!`);
      handleClosePost();
      onSaved && onSaved(saved);
    } catch (e) {
      setStatus("Error: " + e.message);
    }
  };

  const handleSaveEmr = () => {
    save();
  };

  const copyFrom = (h) => {
    setForm((f) => ({
      ...f,
      teeth: f.teeth.length
        ? f.teeth
        : (h.teeth || []).map(({ palmerCode, findingCode, note }) => ({
            palmerCode,
            findingCode,
            note,
          })),
      teethNotes: f.teethNotes || h.teethNotes || "",
      periodontium: f.periodontium || h.periodontium || "",
      oralMucosa: f.oralMucosa || h.oralMucosa || "",
      tmj: f.tmj || h.tmj || "",
    }));
    setShowHistory(false);
  };

  return (
    <div className="emr">
      <header className="emr-head">
        <Box
          sx={{
            whiteSpace: "nowrap",
            lineHeight: 1.25,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="baseline">
            <Typography
              id="dental-emr-title"
              sx={{ fontWeight: 700, color: "#0f766e", fontSize: 14.5 }}
            >
              {patient.name}
            </Typography>
            <Typography sx={{ fontSize: 12.5 }}>
              {patient.age ? patient.age + " y" : ""} {patient.gender || ""}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              MR No {patient.mrNo || "-"} · Patient ID {patient.id} · Visit{" "}
              {visitId}
            </Typography>
            {emrId && (
              <Typography
                sx={{
                  fontSize: 10.5,
                  color: "#b45309",
                  fontWeight: 700,
                  border: "1px solid #b45309",
                  borderRadius: 1,
                  px: 0.6,
                }}
              >
                EDIT · EMR #{emrId}
              </Typography>
            )}
          </Stack>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
            {patient.doctorName}
            {patient.department ? " · " + patient.department : ""}
            {patient.visitDate ? " · " + patient.visitDate : ""}
          </Typography>
        </Box>
        <div className="status">{status}</div>
        <Stack direction="row" spacing={1} alignItems="center">
          <Badge badgeContent={pastCount} color="primary">
            <Button
              size="small"
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setShowHistory((s) => !s)}
            >
              History
            </Button>
          </Badge>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
          >
            Print
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<SaveIcon />}
            // onClick={save}
            onClick={() => {
              setOpenPost(true);
            }}
            sx={{ bgcolor: "#0f766e", "&:hover": { bgcolor: "#0b5d57" } }}
          >
            {emrId ? "Update" : "Save"} EMR
          </Button>

          <IconButton size="small" onClick={onClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </header>

      <main className="emr-main">
        <section className="col">
          <h2>History</h2>
          <TemplateField
            fieldKey="CHIEF_COMPLAINT"
            label="Chief Complaints"
            value={form.chiefComplaint}
            onChange={set("chiefComplaint")}
            rows={2}
          />
          <TemplateField
            fieldKey="HOPI"
            label="HOPI"
            value={form.hopi}
            onChange={set("hopi")}
            rows={3}
          />
          <TemplateField
            fieldKey="PAST_HISTORY"
            label="Past History"
            value={form.pastHistory}
            onChange={set("pastHistory")}
            rows={3}
          />
          <TemplateField
            fieldKey="OTHER_INFO"
            label="Other Relevant Information"
            value={form.otherInfo}
            onChange={set("otherInfo")}
            rows={2}
          />
          <TemplateField
            fieldKey="PHYSICAL_EXAM"
            label="Physical Examination"
            value={form.physicalExam}
            onChange={set("physicalExam")}
            rows={2}
          />
          <TemplateField
            fieldKey="GENERAL_EXAM"
            label="General Examination"
            value={form.generalExam}
            onChange={set("generalExam")}
            rows={2}
          />
        </section>

        <section className="col">
          <h2>
            Intra Oral <small>(Palmer notation)</small>
          </h2>
          <div className="sub">A. Teeth / Edentulous Alveolar Process</div>
          <ToothChart
            findings={form.teeth}
            onChange={set("teeth")}
            master={masters.findings}
          />
          <TemplateField
            fieldKey="TEETH"
            label="Teeth / Alveolar process – findings"
            value={form.teethNotes}
            onChange={set("teethNotes")}
            rows={2}
          />
          <TemplateField
            fieldKey="PERIODONTIUM"
            label="B. Periodontium"
            value={form.periodontium}
            onChange={set("periodontium")}
            rows={2}
          />
        </section>

        <section className="col">
          <h2>Examination &amp; Diagnosis</h2>
          <TemplateField
            fieldKey="ORAL_MUCOSA"
            label="C. Oral Mucous Membrane"
            value={form.oralMucosa}
            onChange={set("oralMucosa")}
            rows={3}
          />
          <TemplateField
            fieldKey="TMJ"
            label="D. TMJ"
            value={form.tmj}
            onChange={set("tmj")}
            rows={2}
          />
          <TemplateField
            fieldKey="DIAGNOSIS"
            label="Diagnosis"
            value={form.diagnosis}
            onChange={set("diagnosis")}
            rows={3}
          />
          <div className="tf" style={{ flex: "0 0 auto" }}>
            <div className="tf-head">
              <label>
                Referral Sequence <small>(click in order)</small>
              </label>
            </div>
            <ReferralChips
              value={form.referrals}
              onChange={set("referrals")}
              master={masters.depts}
            />
            {form.referrals.length > 0 && (
              <div className="refseq">
                {form.referrals
                  .map(
                    (r, i) =>
                      `${i + 1}. ${deptName(r.deptCode)} (${r.deptCode})`,
                  )
                  .join("  →  ")}
              </div>
            )}
          </div>
        </section>
      </main>

      {showHistory && (
        <HistoryPanel
          history={history}
          currentVisitId={visitId}
          deptName={deptName}
          findingName={findingName}
          onClose={() => setShowHistory(false)}
          onCopy={copyFrom}
        />
      )}
      <PrintSheet
        patient={patient}
        visitId={visitId}
        emr={form}
        deptName={deptName}
        findingName={findingName}
      />
      <ConfirmationModal
        confirmationOpen={openPost}
        confirmationHandleClose={handleClosePost}
        confirmationSubmitFunc={handleSaveEmr}
        confirmationLabel="Confirmation "
        confirmationMsg={`Are you sure want to ${
          emrId ? `update` : `Save`
        } this record ?`}
        confirmationButtonMsg={emrId ? "Update" : "Save"}
      />
    </div>
  );
}
